import html2pdf from 'html2pdf.js';

/**
 * Exports DOM content to PDF using html2pdf.js library
 * This is a generic utility that can be used for any DOM element
 */
export async function exportDomToPdf(
  elementRef: React.RefObject<HTMLElement>,
  filename?: string,
  options?: {
    margin?: number;
    format?: 'letter' | 'a4' | 'legal';
    orientation?: 'portrait' | 'landscape';
    quality?: number;
    scale?: number;
    timeout?: number;
  }
): Promise<void> {
  if (!elementRef.current) {
    throw new Error('Element not found');
  }

  try {
    const element = elementRef.current;
    
    // Generate filename with timestamp if not provided
    const currentDate = new Date().toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    }).replace(/\//g, '-');
    
    const finalFilename = filename || `export-${currentDate}.pdf`;

    // Give the browser a moment to fully render the changes
    await new Promise(resolve => setTimeout(resolve, 500));

    // Configure html2pdf options with defaults
    const pdfOptions = {
      margin: options?.margin || 0.5,
      filename: finalFilename,
      image: { type: 'jpeg' as const, quality: options?.quality || 0.8 },
      html2canvas: { 
        scale: options?.scale || 1, 
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: 0,
        logging: false, // Disable logging for performance
        imageTimeout: 0, // Disable image timeout
        removeContainer: true,
        async: true,
        foreignObjectRendering: false // Disable for better compatibility
      },
      jsPDF: { 
        unit: 'in' as const, 
        format: options?.format || 'letter', 
        orientation: options?.orientation || 'portrait' as const,
        compress: true
      },
      pagebreak: { mode: 'css' }
    };

    try {
      // Use html2pdf.js to generate and download the PDF
      await Promise.race([
        new Promise<void>((resolve, reject) => {
          try {
            // Create a wrapper div with the content
            const wrapper = document.createElement('div');
            wrapper.innerHTML = element.innerHTML;
            
            // Apply minimal styles to ensure visibility and proper page breaks
            wrapper.style.cssText = `
              display: block !important;
              visibility: visible !important;
              position: static !important;
              width: 700px !important;
              max-width: 700px !important;
              height: auto !important;
              min-height: 100px !important;
              overflow: visible !important;
              background-color: white !important;
              color: black !important;
              font-family: Arial, sans-serif !important;
              font-size: 11px !important;
              line-height: 1.3 !important;
              padding: 15px !important;
              margin: 0 !important;
              box-sizing: border-box !important;
              page-break-inside: auto !important;
              transform: none !important;
              opacity: 1 !important;
            `;
            
            // Remove any hidden elements and convert print classes
            const allElements = wrapper.querySelectorAll('*');
            allElements.forEach((el) => {
              const htmlEl = el as HTMLElement;
              if (htmlEl.className && typeof htmlEl.className === 'string') {
                htmlEl.className = htmlEl.className
                  .replace(/\bhidden\b/g, '')
                  .replace(/\bprint:(\w+)\b/g, '$1');
              }
              htmlEl.style.display = htmlEl.style.display === 'none' ? 'block' : htmlEl.style.display;
              htmlEl.style.visibility = htmlEl.style.visibility === 'hidden' ? 'visible' : htmlEl.style.visibility;
            });
            
            // Temporarily add to DOM for html2pdf
            document.body.appendChild(wrapper);
            
            html2pdf()
              .set(pdfOptions)
              .from(wrapper)
              .save()
              .then(() => {
                document.body.removeChild(wrapper);
                resolve();
              })
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .catch((error: any) => {
                console.error('html2pdf generation failed:', error);
                if (document.body.contains(wrapper)) {
                  document.body.removeChild(wrapper);
                }
                reject(new Error('PDF generation failed: ' + error.message));
              });
            
          } catch (syncError) {
            console.error('Synchronous error in html2pdf:', syncError);
            reject(syncError);
          }
        }),
        new Promise<never>((_, reject) => {
          const timeoutMs = options?.timeout || 30000; // Default 30 seconds
          setTimeout(() => reject(new Error(`PDF generation timed out after ${timeoutMs / 1000} seconds`)), timeoutMs);
        })
      ]);
    } finally {
      // No cleanup needed since we use a cloned wrapper
    }
    
  } catch (error) {
    console.error('DOM to PDF conversion failed:', error);
    throw error;
  }
}

/**
 * Legacy alias for backward compatibility
 * @deprecated Use exportDomToPdf instead
 */
export const exportDomToPdfDirectly = exportDomToPdf;